/**
 * useContentStrategyDraft — real-time subscription for a draft strategy
 * doc and its nested sprints. Drives StrategyDraftReviewPage.
 *
 * Mirrors useContentStrategy() but exposes draft-specific helpers:
 *   - isDraftPending: strategy.approvalStage === 'draft_pending_approval'
 *   - isApproved:     strategy.approvalStage === 'approved_for_generation'
 *
 * Pattern: uses the shared ContentStrategyFirestoreService for subscriptions
 * (centralises onSnapshot wiring); no direct Firebase imports here.
 */

import { useEffect, useState } from 'react';
import { ContentStrategyFirestoreService } from '../services/ContentStrategyFirestoreService';
import type { ContentStrategy, ContentSprint } from '../types';

export interface UseContentStrategyDraftReturn {
  strategy: ContentStrategy | null;
  sprints: ContentSprint[];
  loading: boolean;
  error: Error | null;
  isDraftPending: boolean;
  isApproved: boolean;
  isGenerated: boolean;
}

let sharedService: ContentStrategyFirestoreService | null = null;
function getService() {
  if (!sharedService) sharedService = new ContentStrategyFirestoreService();
  return sharedService;
}

export function useContentStrategyDraft(
  projectId: string | null,
  strategyId: string | null,
): UseContentStrategyDraftReturn {
  const [strategy, setStrategy] = useState<ContentStrategy | null>(null);
  const [sprints, setSprints] = useState<ContentSprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !strategyId) {
      setStrategy(null);
      setSprints([]);
      setLoading(false);
      return;
    }
    const svc = getService();
    let cancelled = false;
    setLoading(true);
    setError(null);

    let strategyArrived = false;
    let sprintsArrived = false;
    const finalizeLoading = () => {
      if (strategyArrived && sprintsArrived) setLoading(false);
    };

    const unsubStrategy = svc.subscribeStrategy(projectId, strategyId, (s) => {
      if (cancelled) return;
      setStrategy(s);
      strategyArrived = true;
      finalizeLoading();
    });

    const unsubSprints = svc.subscribeSprints(projectId, strategyId, (arr) => {
      if (cancelled) return;
      setSprints(arr);
      sprintsArrived = true;
      finalizeLoading();
    });

    return () => {
      cancelled = true;
      unsubStrategy();
      unsubSprints();
    };
  }, [projectId, strategyId]);

  const approvalStage = (strategy as any)?.approvalStage as string | undefined;

  return {
    strategy,
    sprints,
    loading,
    error,
    isDraftPending: approvalStage === 'draft_pending_approval',
    isApproved: approvalStage === 'approved_for_generation',
    isGenerated: approvalStage === 'generated',
  };
}
